/**
 * Cómo entra y sale contenido del canvas: copiar, pegar y soltar archivos.
 *
 * Se separó de `CanvasView` porque es un tema propio —el portapapeles del
 * sistema, los adjuntos del vault y el duplicado interno— con una frontera
 * angosta: necesita el store, dónde está el puntero y poder cambiar la
 * selección. Todo lo demás (qué está seleccionado, si se está escribiendo)
 * llega por callback.
 */

import { type StoreApi } from "zustand/vanilla";
import { type DocState } from "../model/store.ts";
import { type Card, type Edge, createId } from "../model/types.ts";
import { isPdfPath, suggestImageName } from "../vault/attachments.ts";
import { imageSize } from "./imageTexture.ts";
import { pdfCoverSize } from "./pdfDocument.ts";

export interface CanvasClipboardDeps {
  store: StoreApi<DocState>;
  /** Punto de mundo donde debería caer lo que se pegue (bajo el puntero). */
  pastePoint: () => { x: number; y: number };
  /** Ids de tarjetas seleccionadas ahora. */
  selectedCards: () => ReadonlySet<string>;
  /** Ids de aristas seleccionadas ahora. */
  selectedEdges: () => ReadonlySet<string>;
  /** Reemplaza la selección por lo recién creado. */
  setSelection: (cards: Iterable<string>, edges: Iterable<string>) => void;
  /** ¿El foco está en un campo de texto? Ahí manda el portapapeles del editor. */
  isEditingText: () => boolean;
  /** Guarda un adjunto en el vault y devuelve su ruta. Ausente = sin vault. */
  saveImage?: (blob: Blob, suggestedName: string) => Promise<string>;
}

export class CanvasClipboard {
  private cards: Card[] = [];
  /** Aristas internas a lo copiado: las que tienen sus DOS extremos adentro. */
  private edges: Edge[] = [];

  constructor(private deps: CanvasClipboardDeps) {}

  /** ¿Hay algo copiado internamente? */
  get hasContent(): boolean {
    return this.cards.length > 0;
  }

  copy(): void {
    const { doc } = this.deps.store.getState();
    const selected = this.deps.selectedCards();
    const cards = doc.cards.filter((c) => selected.has(c.id));
    if (cards.length === 0) return;
    this.cards = cards.map((c) => ({ ...c }));
    // Sólo las aristas INTERNAS: una flecha con un extremo afuera de lo copiado
    // no tiene a dónde ir en el pegado, así que se descarta (como en Figma).
    const ids = new Set(cards.map((c) => c.id));
    this.edges = doc.edges
      .filter((e) => ids.has(e.fromNode) && ids.has(e.toNode))
      .map((e) => ({ ...e }));
  }

  paste(): void {
    if (this.cards.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    for (const c of this.cards) {
      minX = Math.min(minX, c.x);
      minY = Math.min(minY, c.y);
    }
    const target = this.deps.pastePoint();
    const dx = target.x - minX;
    const dy = target.y - minY;
    const now = Date.now();

    // Mapa id viejo → id nuevo, para reapuntar lo que se referencie entre sí.
    const remap = new Map<string, string>();
    for (const c of this.cards) remap.set(c.id, createId());

    const created = this.cards.map((c) => ({
      ...c,
      id: remap.get(c.id)!,
      x: c.x + dx,
      y: c.y + dy,
      // Un contenedor copiado tiene que quedarse con SUS hijos nuevos: sin
      // reapuntar, el pegado reclamaba como hijas a las tarjetas originales.
      childrenIds: c.childrenIds
        ?.map((childId) => remap.get(childId))
        .filter((v): v is string => !!v),
      updatedAt: now,
    }));
    const createdEdges: Edge[] = this.edges.map((e) => ({
      ...e,
      id: createId("edge"),
      fromNode: remap.get(e.fromNode)!,
      toNode: remap.get(e.toNode)!,
      updatedAt: now,
    }));

    // Tarjetas y aristas entran juntas: un solo paso de deshacer.
    const store = this.deps.store.getState();
    store.transaction(() => {
      store.addCards(created);
      // Lo pegado adentro de un contenedor pasa a ser suyo, como si se hubiera
      // creado ahí. Los hijos de un contenedor pegado ya tienen dueño y no
      // entran por acá.
      store.adoptIntoGroups(created.map((c) => c.id));
      if (createdEdges.length > 0) store.addEdges(createdEdges);
    });
    this.deps.setSelection(
      created.map((c) => c.id),
      createdEdges.map((e) => e.id),
    );
  }

  /** Manejador del evento `copy` del documento. */
  onCopy = (e: ClipboardEvent): void => {
    if (this.deps.isEditingText() || this.deps.selectedCards().size === 0) return;
    // (las aristas sueltas no se copian solas: el pegado necesita sus nodos)
    e.preventDefault();
    this.copy();
  };

  /** Manejador del evento `paste` del documento. */
  onPaste = (e: ClipboardEvent): void => {
    if (this.deps.isEditingText()) return;
    const items = e.clipboardData?.items;
    // Extract the File objects synchronously — clipboard items are neutered once
    // the event returns, so we can't defer getAsFile() into the async handler.
    const files: File[] = [];
    if (items) {
      for (const it of items) {
        if (it.kind === "file" && (it.type.startsWith("image/") || it.type === "application/pdf")) {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
    }
    if (files.length > 0 && this.deps.saveImage) {
      e.preventDefault();
      void this.addImages(files, this.deps.pastePoint());
      return;
    }
    // No image on the clipboard → fall back to duplicating internally-copied cards.
    if (this.cards.length > 0) {
      e.preventDefault();
      this.paste();
    }
  };

  /** Save each image into the vault and drop a `file` node centred on `anchor`. */
  async addImages(blobs: File[], anchor: { x: number; y: number }): Promise<void> {
    const saveImage = this.deps.saveImage;
    if (!saveImage) return;
    const created: string[] = [];
    let offset = 0;
    for (const blob of blobs) {
      try {
        // Un PDF entra por el mismo camino que una imagen: se guarda en
        // attachments y queda como nodo `file`. La proporción sale de su
        // primera página, que es lo que la tarjeta va a mostrar de portada.
        const isPdf = blob.type === "application/pdf" || isPdfPath(blob.name);
        const { width, height } = isPdf ? await pdfCoverSize(blob) : await imageSize(blob);
        const path = await saveImage(blob, isPdf ? blob.name : suggestImageName(blob.type));
        const w = 360;
        const h = Math.max(80, Math.round((w * height) / Math.max(1, width)));
        const card = this.deps.store.getState().addCard({
          type: "file",
          file: path,
          markdown: "",
          x: anchor.x - w / 2 + offset,
          y: anchor.y - h / 2 + offset,
          width: w,
          height: h,
        });
        created.push(card.id);
        offset += 28; // cascade when pasting several at once
      } catch (err) {
        console.error("paste image failed", err);
      }
    }
    if (created.length > 0) {
      this.deps.store.getState().adoptIntoGroups(created);
      this.deps.setSelection(created, []);
    }
  }
}
